import React, { type ReactNode } from 'react';
import Tooltip, {
  TOOLTIP_ALIGN_CENTER,
  TOOLTIP_PLACEMENT_BOTTOM,
  TOOLTIP_SIZE_SMALL,
  type TooltipAlign,
  type TooltipPlacement,
  type TooltipSize,
} from '../Tooltip';
import {
  ICON_BUTTON_SIZE_MD,
  ICON_BUTTON_VARIANT_GHOST,
  type IconButtonSize,
  type IconButtonVariant,
} from './IconButton.constants';
import { Root } from './IconButton.styled';

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Required for screen readers — there is no visible label. */
  ariaLabel: string;
  /** The icon to render — Material symbol span, react-icons element, raw svg. */
  icon: ReactNode;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  /**
   * Pin the "active/open" surface treatment (used while a menu the button
   * triggers is open). Independent of `:hover` so the button keeps its open
   * state even when the cursor moves to the menu content.
   */
  isOpen?: boolean;
  /** Makes the button a toggle: the `isOpen` surface while on, and `aria-pressed` either way. */
  pressed?: boolean;
  /**
   * Optional hover/focus tooltip body. When provided, the button is wrapped
   * in `<Tooltip portal>` so the tip floats over `overflow: hidden` ancestors
   * (e.g. floating toolbars). Pass plain text for the default behaviour or
   * any react node for richer content. The native `title` attribute is
   * intentionally NOT set when this prop is present so callers don't get
   * a double tooltip.
   */
  tooltip?: ReactNode;
  /**
   * Tooltip placement relative to the button. Defaults to `bottom` so the
   * tip floats away from the trigger toward the canvas — flip to `top` for
   * buttons sitting at the bottom of a floating panel.
   */
  tooltipPlacement?: TooltipPlacement;
  /** Which edge of the tip lines up with the button: `end` keeps a tip by the viewport's right edge on screen. */
  tooltipAlign?: TooltipAlign;
  tooltipSize?: TooltipSize;
  /** Where the tooltip renders; defaults to `document.body`. Pass a node inside a shadow root to keep its styles. */
  tooltipPortalContainer?: Element | null;
  dataTestId?: string;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      ariaLabel,
      icon,
      size = ICON_BUTTON_SIZE_MD,
      variant = ICON_BUTTON_VARIANT_GHOST,
      isOpen = false,
      pressed,
      tooltip,
      tooltipPlacement = TOOLTIP_PLACEMENT_BOTTOM,
      tooltipAlign = TOOLTIP_ALIGN_CENTER,
      tooltipSize = TOOLTIP_SIZE_SMALL,
      tooltipPortalContainer,
      type = 'button',
      dataTestId = 'icon-button',
      ...rest
    },
    ref,
  ) {
    const button = (
      <Root
        ref={ref}
        type={type}
        aria-label={ariaLabel}
        $size={size}
        $variant={variant}
        $isOpen={isOpen || pressed === true}
        aria-pressed={pressed ?? (isOpen || undefined)}
        data-testid={dataTestId}
        {...rest}
      >
        {icon}
      </Root>
    );

    // Treat every "renders nothing" ReactNode as "no tooltip" so callers
    // can safely pass `condition && 'msg'` (which evaluates to `false`
    // when off) without getting an empty/ghost popup. We explicitly
    // preserve `0` because that's a legitimate, render-able ReactNode.
    if (
      tooltip === undefined ||
      tooltip === null ||
      tooltip === false ||
      tooltip === ''
    ) {
      return button;
    }

    return (
      <Tooltip
        content={tooltip}
        placement={tooltipPlacement}
        align={tooltipAlign}
        size={tooltipSize}
        portal
        portalContainer={tooltipPortalContainer}
        dataTestId={`${dataTestId}__tooltip`}
      >
        {button}
      </Tooltip>
    );
  },
);

export default IconButton;
